import AgGridReactTable from "@components/ag-grid/AgGridReactTable";
import AgGridSkeleton from "@components/ag-grid/AgGridSkeleton";
import { affiliateApis } from "@services/apis/Affiliate";
import {
  GetAffiliatesData,
  GetAffiliatesResponse,
} from "@services/models/Affiliate/GetAffilatesModel";
import {
  GetAffiliateMerchantsData,
  GetAffiliateMerchantsResponse,
} from "@services/models/Affiliate/GetAffiliateMerchantsModel";
import { ColDef } from "ag-grid-community";
import { useCallback, useEffect, useRef, useState } from "react";
import ConstantFunctions from "../../utils/constants_functions";
import MerchantCommisionsSlider from "./components/MerchantCommisionsSlider";

const CurrentAffiliates = () => {
  const constantFunctions = new ConstantFunctions();
  const affiliatesGridRef = useRef<any>(null);
  const merchantsGridRef = useRef<any>(null);

  const [affiliates, setAffiliates] = useState<GetAffiliatesData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const rowsPerPage = 1000;

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [merchants, setMerchants] = useState<GetAffiliateMerchantsData[]>([]);
  const [merchantsLoading, setMerchantsLoading] = useState<boolean>(false);
  const [merchantsError, setMerchantsError] = useState<string>("");

  const [editMerchantId, setEditMerchantId] = useState<number | null>(null);
  const [editMerchantEmail, setEditMerchantEmail] = useState<string>("");
  const [isEditMerchantSliderOpen, setIsEditMerchantSliderOpen] =
    useState<boolean>(false);

  // Column definitions for affiliates grid
  const affiliatesColumnDefs: ColDef[] = [
    {
      headerName: "Affiliate User ID",
      field: "userId",
      headerClass: "ag-header-cell-custom",
      cellClass: "ag-cell-custom",
      flex: 1,
      minWidth: 120,
      maxWidth: 180,
    },
    {
      headerName: "Email",
      field: "emailId",
      headerClass: "ag-header-cell-custom",
      cellClass: "ag-cell-custom",
      flex: 2,
      minWidth: 240,
      maxWidth: 320,
    },
    {
      headerName: "Merchant Count",
      field: "affiliatedMerchantsCount",
      headerClass: "ag-header-cell-custom",
      cellClass: "ag-cell-custom",
      flex: 3,
      minWidth: 180,
      maxWidth: 240,
    },
  ];

  // Column definitions for merchants grid
  const merchantsColumnDefs: ColDef[] = [
    {
      headerName: "Mapped Date",
      field: "createdAt",
      headerClass: "ag-header-cell-custom",
      cellClass: "ag-cell-custom",
      flex: 1,
      minWidth: 100,
      maxWidth: 140,
      cellRenderer: (params: any) => {
        return constantFunctions.dateFormatInDDMMYY(params.value);
      },
    },
    {
      headerName: "Merchant Email ID",
      field: "merchantEmail",
      headerClass: "ag-header-cell-custom",
      cellClass: "ag-cell-custom",
      flex: 2,
      minWidth: 180,
      maxWidth: 280,
    },
    {
      headerName: "Rates",
      field: "id",
      headerClass: "ag-header-cell-custom",
      cellClass: "ag-cell-custom",
      flex: 0.6,
      minWidth: 60,
      maxWidth: 100,
      cellRenderer: (params: any) => {
        return (
          <div className="flex justify-start">
            <div
              className="flex justify-center items-center cursor-pointer shadow-[0px_2px_4px_0px_rgba(0,0,0,0.08)] bg-border-button-secondary border border-bg-button-secondary"
              style={{ width: "20px", height: "20px", borderRadius: "4px" }}
              onClick={() => {
                setEditMerchantId(params.data.id);
                setEditMerchantEmail(params.data.merchantEmail);
                setIsEditMerchantSliderOpen(true);
              }}
            >
              <span className="text-[14px] material-symbols-outlined text-text-base">
                border_color
              </span>
            </div>
          </div>
        );
      },
    },
  ];

  // Add global function for handling edit merchant action
  useEffect(() => {
    (window as any).handleEditMerchant = (
      merchantId: number,
      merchantEmail: string,
    ) => {
      setEditMerchantId(merchantId);
      setEditMerchantEmail(merchantEmail);
      setIsEditMerchantSliderOpen(true);
    };

    return () => {
      delete (window as any).handleEditMerchant;
    };
  }, []);

  const onAffiliatesBodyScroll = (params: any) => {
    const scrollTop = params.top;
    const scrollHeight = params.api.getDisplayedRowCount() * 48;
    const clientHeight =
      params.api.getVerticalPixelRange().bottom -
      params.api.getVerticalPixelRange().top;

    if (scrollTop + clientHeight >= scrollHeight - 100 && hasMore && !loading) {
      loadMoreAffiliates();
    }
  };

  const onAffiliateRowClick = (params: any) => {
    const selectedData = params.data;

    const allRows = document.querySelectorAll(".ag-row");
    allRows.forEach((row) => {
      row.classList.remove("ag-row-selected-custom");
    });

    if (params.rowIndex !== null) {
      const rowElement = document.querySelector(
        `[row-index="${params.rowIndex}"]`,
      );
      if (rowElement) {
        rowElement.classList.add("ag-row-selected-custom");
      }
    }

    setSelectedId(selectedData.id);
  };

  const fetchAffiliatesList = useCallback(
    async (pageNumber: number, append: boolean) => {
      try {
        setLoading(true);
        const response: GetAffiliatesResponse =
          await affiliateApis.getAffiliates({
            page: pageNumber,
            limit: rowsPerPage,
          });

        const fetched = response.data;
        setAffiliates((prev) => (append ? [...prev, ...fetched] : fetched));
        setHasMore(fetched.length === rowsPerPage);
      } catch (err: any) {
        setError("Failed to fetch affiliates data");
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const loadMoreAffiliates = useCallback(() => {
    if (loading || !hasMore) return;
    setPage((prev) => {
      const next = prev + 1;
      fetchAffiliatesList(next, true);
      return next;
    });
  }, [loading, hasMore, fetchAffiliatesList]);

  const fetchMerchantsList = async (affiliateId: number) => {
    try {
      setMerchantsLoading(true);
      setMerchantsError("");
      const response: GetAffiliateMerchantsResponse =
        await affiliateApis.getMerchantAffiliates({
          id: affiliateId,
        });

      setMerchants(response.data);
    } catch (err: any) {
      setMerchantsError("Failed to fetch merchants data");
    } finally {
      setMerchantsLoading(false);
    }
  };

  useEffect(() => {
    setPage(1);
    setHasMore(true);
    fetchAffiliatesList(1, false);
  }, [fetchAffiliatesList]);

  useEffect(() => {
    if (selectedId) {
      fetchMerchantsList(selectedId);
    }
  }, [selectedId]);

  return (
    <>
      <div className="flex flex-row gap-3 items-start">
        <div
          className="flex flex-col bg-bg-page-body py-[20px] overflow-hidden gap-4 w-[60%]"
          style={{ height: "calc(100dvh - 120px)" }}
        >
          <div className="rounded-lg" style={{ height: "100%" }}>
            {loading && affiliates.length === 0 ? (
              <AgGridSkeleton
                columnDefs={affiliatesColumnDefs}
                style={{ height: "100%", width: "100%" }}
              />
            ) : (
              <AgGridReactTable
                columnDefs={affiliatesColumnDefs}
                rowData={affiliates}
                theme="legacy"
                onBodyScroll={onAffiliatesBodyScroll}
                onRowClicked={onAffiliateRowClick}
                getRowClass={(params) =>
                  selectedId === params.data.id ? "ag-row-selected-custom" : ""
                }
                ref={affiliatesGridRef}
                error={error}
              />
            )}
          </div>
        </div>

        <div
          className="flex flex-col py-[16px] px-[16px] bg-bg-base rounded-lg mt-[20px] w-[40%]"
          style={{ height: "40dvh" }}
        >
          {selectedId ? (
            <div className="flex flex-col gap-2 h-full">
              <div className="flex flex-row items-center justify-between mb-4">
                <p className="text-text-base text-[14px] font-medium">
                  Mapped Merchants
                </p>
                <p className="text-text-base text-[14px] font-medium">
                  {merchants.length}
                </p>
              </div>
              <div className="overflow-hidden" style={{ height: "100%" }}>
                {merchantsLoading && merchants.length === 0 ? (
                  <AgGridSkeleton
                    columnDefs={merchantsColumnDefs}
                    style={{ height: "100%", width: "100%" }}
                  />
                ) : (
                  <AgGridReactTable
                    columnDefs={merchantsColumnDefs}
                    rowData={merchants}
                    theme="legacy"
                    ref={merchantsGridRef}
                    error={merchantsError}
                  />
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-text-base text-[12px]">
              Select an affiliate to view merchants
            </div>
          )}
        </div>
        <MerchantCommisionsSlider
          isOpen={isEditMerchantSliderOpen}
          onClose={() => setIsEditMerchantSliderOpen(false)}
          id={editMerchantId!}
          merchantName={editMerchantEmail}
        />
      </div>
    </>
  );
};

export default CurrentAffiliates;
